// Leva ao servidor o que foi feito no aparelho e traz de volta o que mudou lá.
// Funciona em ciclos: envia a fila na ordem e depois baixa as tabelas da
// empresa. Sem sinal, o ciclo para no primeiro erro de rede e tenta de novo
// quando o sinal volta (evento "online", app voltando para a tela, a cada 30 s
// enquanto houver algo esperando, ou logo depois de cada gravação).
//
// Um ciclo por vez em todas as abas (Web Locks), para o mesmo item não subir
// duas vezes. Reenviar também não duplica: criar ignora id repetido e alterar
// manda os mesmos campos.
import { isAuthRetryableFetchError } from '@supabase/supabase-js';
import { codigoDoErro, mensagemDeErro } from '../lib/erros.js';
import { registrarErro } from '../lib/registro-de-erros.js';
import { supabase } from '../supabase.js';
import * as armazem from './armazem.js';
import { TABELAS, donoDoBanco, gravarMeta, lerMeta, temBanco } from './banco.js';

export const estado = {
  online: typeof navigator === 'undefined' || navigator.onLine !== false,
  enviando: false,
  pendentes: 0,
  recusadas: 0,
};

const eventos = new EventTarget();
let empresaAtual = () => null;

export function aoMudar(fn) {
  eventos.addEventListener('mudou', fn);
  return () => eventos.removeEventListener('mudou', fn);
}

// Chamado quando uma baixa trouxe dados novos do servidor (outro aparelho
// mudou algo, alguém assinou pelo link…).
export function aoChegarDados(fn) {
  eventos.addEventListener('dados', fn);
  return () => eventos.removeEventListener('dados', fn);
}

// O servidor recusou um envio por falta de permissão (42501).
export function aoPerderPermissao(fn) {
  eventos.addEventListener('permissao', fn);
  return () => eventos.removeEventListener('permissao', fn);
}

function avisarMudanca() {
  eventos.dispatchEvent(new Event('mudou'));
}

function definirOnline(valor) {
  if (estado.online === valor) return;
  estado.online = valor;
  avisarMudanca();
}

export async function atualizarResumo() {
  if (!temBanco()) return;
  const { pendentes, recusadas } = await armazem.resumoDaFila();
  if (pendentes === estado.pendentes && recusadas === estado.recusadas) return;
  estado.pendentes = pendentes;
  estado.recusadas = recusadas;
  avisarMudanca();
}

// ---------------------------------------------------------------------
// Tipos de erro
// ---------------------------------------------------------------------
export function ehErroDeRede(erro) {
  if (!erro) return false;
  if (erro.name === 'AuthRetryableFetchError' || erro instanceof TypeError) return true;
  return /failed to fetch|networkerror|load failed|fetch failed|network request failed|err_internet_disconnected|aborterror|timeout/i.test(
    String(erro.message ?? erro),
  );
}

function ehTemporario(erro, status) {
  return ehErroDeRede(erro) || status === 0 || status === 408 || status === 429 || status >= 500;
}

function ehSessaoVencida(erro, status) {
  return status === 401 || erro?.code === 'PGRST301' || erro?.code === 'PGRST303' || /jwt expired/i.test(erro?.message ?? '');
}

// 'ok' | 'sem-rede' | 'sem-sessao'. Só é 'ok' se a sessão for do mesmo
// usuário dono do banco do aparelho (a fila de um nunca sobe com a sessão de
// outro).
async function garantirSessao(renovar = false) {
  try {
    const { data, error } = renovar ? await supabase.auth.refreshSession() : await supabase.auth.getSession();
    if (data?.session) {
      if (data.session.user?.id !== donoDoBanco()) {
        console.warn('Sincronia: a sessão é de outro usuário; nada é enviado.');
        return 'sem-sessao';
      }
      return 'ok';
    }
    if (error && (isAuthRetryableFetchError(error) || ehErroDeRede(error))) return 'sem-rede';
    return 'sem-sessao';
  } catch (erro) {
    return ehErroDeRede(erro) ? 'sem-rede' : 'sem-sessao';
  }
}

// ---------------------------------------------------------------------
// Envio de um item da fila
// ---------------------------------------------------------------------
async function buscarNoServidor(tabela, id) {
  const r = await supabase.from(tabela).select('*').eq('id', id).maybeSingle();
  return r.error ? { erro: r.error, status: r.status } : { linha: r.data ?? null };
}

// Foto (ou PDF) de um anexo: sobe o arquivo antes de gravar o anexo. Sem
// sobrescrever (o comprovante é prova): se já estava lá, o envio anterior
// chegou.
const jaExiste = (erro) => String(erro?.statusCode ?? erro?.status) === '409' || /already exists|duplicate/i.test(erro?.message ?? '');

async function enviarArquivo(item) {
  const registro = await armazem.arquivo(item.registro_id);
  if (!registro || registro.enviado) return null;
  // Vai o conteúdo cru (não formulário), com o tipo no cabeçalho.
  const { error } = await supabase.storage
    .from('comprovantes')
    .upload(item.dados.storage_path, await registro.blob.arrayBuffer(), { contentType: registro.mime, upsert: false });
  if (error && !jaExiste(error)) return { erro: error, status: Number(error.statusCode ?? error.status) || 0 };
  await armazem.marcarArquivoEnviado(item.registro_id);
  return null;
}

async function enviarItem(item) {
  const { tabela, tipo, registro_id: id } = item;
  let { dados } = item;
  if (tabela === 'anexos' && tipo === 'criar') {
    const falha = await enviarArquivo(item);
    if (falha) return falha;
    dados = { ...dados, enviado_em: dados.enviado_em ?? new Date().toISOString() };
  }
  if (tipo === 'criar') {
    const r = await supabase.from(tabela).upsert(dados, { onConflict: 'id', ignoreDuplicates: true }).select();
    if (r.error) {
      // Uma tentativa anterior pode ter chegado (e a resposta se perdido): as
      // regras do banco rodam antes de ver que o id já existe e podem recusar
      // o reenvio. Se o registro está lá, a criação valeu.
      if (!ehTemporario(r.error, r.status) && !ehSessaoVencida(r.error, r.status)) {
        const busca = await buscarNoServidor(tabela, id);
        if (busca.linha) return { linha: busca.linha };
        // Não deu para conferir (sinal caiu): tenta tudo de novo depois, em
        // vez de dar como recusada uma criação que pode estar lá.
        if (busca.erro && ehTemporario(busca.erro, busca.status)) return busca;
      }
      return { erro: r.error, status: r.status };
    }
    if (r.data?.length) return { linha: r.data[0] };
    // Já estava lá: o envio anterior chegou, mas a resposta se perdeu.
    const busca = await buscarNoServidor(tabela, id);
    if (busca.erro) return busca;
    if (busca.linha) return { linha: busca.linha };
    return { erro: { code: 'PGRST116', message: 'O registro não foi gravado.' }, status: 406 };
  }
  const r = await supabase.from(tabela).update(dados).eq('id', id).select();
  if (r.error) return { erro: r.error, status: r.status };
  if (!r.data?.length) return { erro: { code: 'PGRST116', message: 'Nenhuma linha alterada.' }, status: 406 };
  return { linha: r.data[0] };
}

// Envia a fila. Devolve 'ok' (fila vazia ou só recusados), 'sem-rede' ou
// 'sem-sessao'.
async function enviarFila() {
  let enviouAlgo = false;
  let renovacoes = 0;
  for (let rodada = 0; rodada < 3; rodada++) {
    for (;;) {
      const item = await armazem.pegarParaEnviar();
      if (!item) break;
      const { linha, erro, status } = await enviarItem(item);
      if (!erro) {
        await armazem.confirmarEnvio(item, linha);
        enviouAlgo = true;
        definirOnline(true);
        await atualizarResumo();
        continue;
      }
      if (ehSessaoVencida(erro, status) && renovacoes < 2) {
        renovacoes += 1;
        await armazem.marcar(item.seq, 'pendente');
        const sessao = await garantirSessao(true);
        if (sessao !== 'ok') return sessao;
        continue;
      }
      if (ehTemporario(erro, status)) {
        await armazem.marcar(item.seq, 'pendente');
        return 'sem-rede';
      }
      await armazem.marcar(item.seq, 'recusada', { codigo: erro.code ?? null, regra: codigoDoErro(erro), mensagem: mensagemDeErro(erro) });
      registrarErro(erro, { onde: `envio:${item.tabela}` });
      await atualizarResumo();
      if (erro.code === '42501') eventos.dispatchEvent(new Event('permissao'));
    }
    // Algo foi aceito: quem tinha sido recusado por depender dele (registro
    // que ainda não existia no servidor) tenta de novo.
    if (!enviouAlgo || !(await armazem.reabrirDependentes())) break;
    enviouAlgo = false;
  }
  return 'ok';
}

// ---------------------------------------------------------------------
// Baixa das tabelas
// ---------------------------------------------------------------------
async function puxarTabela(tabela, empresaId) {
  const tamanho = 1000;
  const linhas = [];
  for (let de = 0; ; de += tamanho) {
    let consulta = supabase.from(tabela).select('*');
    // Categorias: as do sistema (sem empresa) e as da empresa.
    consulta = tabela === 'categorias'
      ? consulta.or(`empresa_id.is.null,empresa_id.eq.${empresaId}`)
      : consulta.eq('empresa_id', empresaId);
    const { data, error, status } = await consulta.order('id').range(de, de + tamanho - 1);
    if (error) throw Object.assign(error, { status });
    linhas.push(...data);
    if (data.length < tamanho) break;
  }
  return armazem.mesclar(tabela, empresaId, linhas);
}

export async function puxar(empresaId, tabelas = TABELAS) {
  let mudou = 0;
  for (const tabela of tabelas) mudou += await puxarTabela(tabela, empresaId);
  if (tabelas === TABELAS) await gravarMeta(`baixado:${empresaId}`, new Date().toISOString());
  definirOnline(true);
  if (mudou > 0) eventos.dispatchEvent(new Event('dados'));
  return mudou;
}

// Baixa já algumas tabelas (depois de ativar placa, descartar alteração…),
// sem cruzar com um envio em andamento: senão a baixa, lida antes do envio
// chegar, poderia gravar por cima a versão velha.
export function puxarAgora(empresaId, tabelas) {
  return exclusivo(() => puxar(empresaId, tabelas));
}

// Contas fixas: o servidor gera a despesa do mês que ainda não existe. Uma
// vez por dia (ou logo depois de mexer numa conta fixa).
const hojeNoAparelho = () => new Date().toLocaleDateString('sv');

async function gerarContasFixas(empresaId) {
  const chave = `fixas:${empresaId}`;
  if ((await lerMeta(chave)) === hojeNoAparelho()) return;
  const { error, status } = await supabase.rpc('gerar_despesas_fixas', { p_empresa: empresaId });
  if (error && ehTemporario(error, status)) throw Object.assign(error, { status });
  // Deu certo, ou a conta não pode lançar agora (só consulta): amanhã tem mais.
  if (error) console.warn('Contas fixas:', error.message);
  await gravarMeta(chave, hojeNoAparelho());
}

// Fotos que já subiram há tempo saem do aparelho (uma vez por dia).
async function limparFotosAntigas() {
  if ((await lerMeta('limpeza')) === hojeNoAparelho()) return;
  await armazem.limparArquivosEnviados();
  await gravarMeta('limpeza', hojeNoAparelho());
}

export async function gerarContasFixasLogo(empresaId) {
  if (temBanco()) await gravarMeta(`fixas:${empresaId}`, null);
  sincronizarLogo({ puxar: true, atraso: 0 });
}

export async function jaBaixou(empresaId) {
  return Boolean(await lerMeta(`baixado:${empresaId}`));
}

// ---------------------------------------------------------------------
// Ciclo
// ---------------------------------------------------------------------
// Uma coisa por vez, em todas as abas: o ciclo de envio e baixa, e quem
// grava no aparelho o que acabou de ler do servidor (baixa avulsa, contas do
// usuário). Nunca chamar de dentro de outro `exclusivo` (travaria).
export function exclusivo(fn) {
  if (typeof navigator !== 'undefined' && navigator.locks?.request) {
    return navigator.locks.request('maxcargo-sincronia', fn);
  }
  return fn();
}

// Envia a fila. Devolve 'ok', 'sem-rede' ou 'sem-sessao'.
async function enviar() {
  await armazem.destravarEnvios();
  await atualizarResumo();
  const sessao = await garantirSessao();
  if (sessao !== 'ok') return sessao;
  estado.enviando = estado.pendentes > 0;
  if (estado.enviando) avisarMudanca();
  try {
    return await enviarFila();
  } catch (erro) {
    if (ehTemporario(erro, erro?.status)) return 'sem-rede';
    throw erro;
  } finally {
    if (estado.enviando) {
      estado.enviando = false;
      avisarMudanca();
    }
    await atualizarResumo();
  }
}

// Devolve { online } e, se a baixa falhou por outro motivo que não o sinal,
// { online: true, erroNaBaixa } (o envio valeu do mesmo jeito).
async function umCiclo({ puxar: baixar }) {
  if (!temBanco()) return { online: estado.online };
  return exclusivo(async () => {
    if (!temBanco()) return { online: estado.online };
    const envio = await enviar();
    if (envio !== 'ok') {
      if (envio === 'sem-rede') definirOnline(false);
      return { online: false };
    }
    await limparFotosAntigas().catch((erro) => console.warn('Limpeza das fotos:', erro));
    const empresaId = empresaAtual();
    if (baixar && empresaId) {
      try {
        await gerarContasFixas(empresaId);
        await puxar(empresaId);
      } catch (erro) {
        if (ehTemporario(erro, erro?.status)) {
          definirOnline(false);
          return { online: false };
        }
        console.error('Sincronia: não foi possível baixar os dados:', erro);
        return { online: true, erroNaBaixa: erro };
      }
    }
    definirOnline(true);
    return { online: true };
  });
}

let rodando = null;
let pedidoDeNovo = null;

// Roda um ciclo agora (ou espera o que está rodando e roda outro depois,
// se alguém pediu no meio). Devolve { online }.
export function sincronizar({ puxar: baixar = true } = {}) {
  if (rodando) {
    pedidoDeNovo = { puxar: baixar || Boolean(pedidoDeNovo?.puxar) };
    return rodando;
  }
  rodando = (async () => {
    try {
      let resultado = await umCiclo({ puxar: baixar });
      while (pedidoDeNovo && resultado.online) {
        const pedido = pedidoDeNovo;
        pedidoDeNovo = null;
        resultado = await umCiclo(pedido);
      }
      return resultado;
    } finally {
      pedidoDeNovo = null;
      rodando = null;
    }
  })();
  return rodando;
}

let temporizador = null;
let logoComBaixa = false;

// Pedido "sem pressa": junta várias gravações seguidas num ciclo só (se
// algum dos pedidos juntados queria baixar, o ciclo baixa).
export function sincronizarLogo({ puxar: baixar = false, atraso = 300 } = {}) {
  atualizarResumo().catch(() => {});
  logoComBaixa = logoComBaixa || baixar;
  clearTimeout(temporizador);
  temporizador = setTimeout(() => {
    const comBaixa = logoComBaixa;
    logoComBaixa = false;
    sincronizar({ puxar: comBaixa }).catch((erro) => console.warn('Sincronia:', erro));
  }, atraso);
}

// Antes de uma ação que só funciona com internet (ativar placa…): envia a fila
// e confirma que há sinal. Sem sinal, lança um erro com a frase para o usuário.
export async function exigirConexao(acao) {
  const { online } = await sincronizar({ puxar: false });
  if (!online) {
    throw new Error(`SEM_INTERNET: sem sinal agora. Para ${acao} é preciso internet; tente de novo quando o sinal voltar.`);
  }
}

// ---------------------------------------------------------------------
// Ligação com o app
// ---------------------------------------------------------------------
let ligado = false;

export function ligarSincronia({ empresa }) {
  empresaAtual = empresa;
  if (ligado || typeof window === 'undefined') return;
  ligado = true;
  window.addEventListener('online', () => {
    // O supabase-js guarda por 1 minuto a última falha ao renovar a sessão
    // (inclusive a de quando estava sem sinal). Com o sinal de volta, vale
    // tentar na hora.
    try {
      supabase.auth.lastRefreshFailure = null;
    } catch {
      /* versão sem esse campo */
    }
    sincronizarLogo({ puxar: true, atraso: 0 });
  });
  window.addEventListener('offline', () => definirOnline(false));
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') sincronizarLogo({ puxar: true, atraso: 0 });
  });
  setInterval(() => {
    if (estado.pendentes > 0 && !rodando) sincronizarLogo({ atraso: 0 });
  }, 30000);
}

// Para os testes e para quando o usuário sai.
export function zerarEstado() {
  estado.pendentes = 0;
  estado.recusadas = 0;
  estado.enviando = false;
  avisarMudanca();
}
